import React, { useMemo, useState } from 'react';
import { Calculator, Phone, Info, ArrowRight } from 'lucide-react';
type Option = {
  value: string;
  label: string;
  factor: number;
};
const diagnoses: Option[] = [{
  value: 'pleural',
  label: 'Pleural mesothelioma',
  factor: 1_400_000
}, {
  value: 'peritoneal',
  label: 'Peritoneal mesothelioma',
  factor: 1_650_000
}, {
  value: 'lungCancer',
  label: 'Asbestos-related lung cancer',
  factor: 900_000
}, {
  value: 'asbestosis',
  label: 'Asbestosis / pleural disease',
  factor: 500_000
}];
const claimTypes: Option[] = [{
  value: 'injury',
  label: 'Personal injury (patient living)',
  factor: 1
}, {
  value: 'wrongfulDeath',
  label: 'Wrongful death (family filing)',
  factor: 0.88
}];
const exposures: Option[] = [{
  value: 'shipyard',
  label: 'Shipyard / U.S. Navy',
  factor: 1.15
}, {
  value: 'refinery',
  label: 'Refinery / chemical plant',
  factor: 1.12
}, {
  value: 'manufacturing',
  label: 'Steel mill / manufacturing',
  factor: 1.08
}, {
  value: 'construction',
  label: 'Construction / building trades',
  factor: 1.05
}, {
  value: 'mechanic',
  label: 'Auto mechanic / brake work',
  factor: 0.95
}, {
  value: 'secondary',
  label: 'Take-home (family member)',
  factor: 1.0
}];
const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});
function formatRange(value: number) {
  return currency.format(Math.round(value / 10_000) * 10_000);
}
export function SettlementCalculator() {
  const [diagnosis, setDiagnosis] = useState(diagnoses[0].value);
  const [claimType, setClaimType] = useState(claimTypes[0].value);
  const [exposure, setExposure] = useState(exposures[0].value);
  const [age, setAge] = useState(68);
  const [years, setYears] = useState(15);
  const [employers, setEmployers] = useState(3);
  const [isVeteran, setIsVeteran] = useState(false);
  const estimate = useMemo(() => {
    const base = diagnoses.find(d => d.value === diagnosis)?.factor ?? 1_400_000;
    const claim = claimTypes.find(c => c.value === claimType)?.factor ?? 1;
    const job = exposures.find(e => e.value === exposure)?.factor ?? 1;
    const ageFactor = Math.min(1.35, Math.max(0.78, 1 + (65 - age) * 0.012));
    const yearsFactor = 1 + Math.min(years, 30) * 0.008;
    const employerFactor = 1 + (employers - 1) * 0.06;
    const veteranFactor = isVeteran ? 1.06 : 1;
    const midpoint = base * claim * job * ageFactor * yearsFactor * employerFactor * veteranFactor;
    return {
      low: midpoint * 0.65,
      high: midpoint * 1.45
    };
  }, [diagnosis, claimType, exposure, age, years, employers, isVeteran]);
  const selectClass = 'w-full px-4 py-3 rounded-md bg-white/5 border border-white/20 text-white focus:ring-2 focus:ring-brand-accent focus:border-brand-accent outline-none transition-all [&>option]:text-slate-900';
  const labelClass = 'block text-sm font-semibold text-brand-100 mb-2';
  return <section id="calculator" className="py-20 md:py-24 bg-brand-900 text-white" data-id="element-263" data-mp="16ndyir441fg2">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-264" data-mp="2r5g38ndu3s4g">
        <div className="max-w-3xl mb-14" data-id="element-265" data-mp="2r3lipa6h620w">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-brand-accent text-sm font-semibold tracking-wide mb-5" data-id="element-266" data-mp="2n3l67v2xs0wh">
            <Calculator className="w-4 h-4" data-id="element-267" data-mp="2d5x0ka3sl8zj" />
            <span data-id="element-268" data-mp="2d78dp0j4nt3b">Free Claim Estimator</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4" data-id="element-269" data-mp="3atfjgdr63rh9">
            Mesothelioma Settlement Calculator
          </h2>
          <p className="text-lg text-brand-100 leading-relaxed" data-id="element-270" data-mp="35v48u0t1tcxz">
            Most California mesothelioma settlements fall between{' '}
            <strong className="text-brand-accent font-semibold" data-id="element-271" data-mp="311jag7njmgy9">
              $1 million and $2.4 million
            </strong>
            , while cases that go to trial have produced verdicts well above $10
            million. Answer a few questions to see an estimated compensation
            range for your claim.
          </p>
        </div>

        <div className="grid lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] gap-8 items-start" data-id="element-272" data-mp="39adste1l1mb7">
          {/* Inputs */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8 space-y-6" data-id="element-273" data-mp="3delj3fnovabj">
            <div className="grid sm:grid-cols-2 gap-6" data-id="element-274" data-mp="2zekim1bc64wl">
              <div data-id="element-275" data-mp="34c3wh044gfo0">
                <label htmlFor="calc-diagnosis" className={labelClass} data-id="element-276" data-mp="34bkitvli8dfs">
                  Diagnosis
                </label>
                <select id="calc-diagnosis" value={diagnosis} onChange={e => setDiagnosis(e.target.value)} className={selectClass} data-id="element-277" data-mp="3s2ggvagk04vh">
                  {diagnoses.map(d => <option key={d.value} value={d.value} data-id="element-278" data-mp="3nag9wy22gbpe">
                      {d.label}
                    </option>)}
                </select>
              </div>

              <div data-id="element-279" data-mp="jlv4rksmkvo5">
                <label htmlFor="calc-claim" className={labelClass} data-id="element-280" data-mp="jmeon71u5u4t">
                  Type of claim
                </label>
                <select id="calc-claim" value={claimType} onChange={e => setClaimType(e.target.value)} className={selectClass} data-id="element-281" data-mp="24az6xsw3wc0t">
                  {claimTypes.map(c => <option key={c.value} value={c.value} data-id="element-282" data-mp="2930dcy6lf8ix">
                      {c.label}
                    </option>)}
                </select>
              </div>
            </div>

            <div data-id="element-283" data-mp="1gfcxtxk6j835">
              <label htmlFor="calc-exposure" className={labelClass} data-id="element-284" data-mp="1gfwfdd5x3ugn">
                Where did the asbestos exposure occur?
              </label>
              <select id="calc-exposure" value={exposure} onChange={e => setExposure(e.target.value)} className={selectClass} data-id="element-285" data-mp="1ubzxi83ioccr">
                {exposures.map(e => <option key={e.value} value={e.value} data-id="element-286" data-mp="30tmyo6rymxye">
                    {e.label}
                  </option>)}
              </select>
            </div>

            <div data-id="element-287" data-mp="23flj0x4lhf29">
              <div className="flex justify-between items-baseline mb-2" data-id="element-288" data-mp="23f231lyi90qv">
                <label htmlFor="calc-age" className="text-sm font-semibold text-brand-100" data-id="element-289" data-mp="27hyfsw1d62mw">
                  Age at diagnosis
                </label>
                <span className="text-brand-accent font-bold" data-id="element-290" data-mp="2cg0887kojeb1">{age}</span>
              </div>
              <input id="calc-age" type="range" min={30} max={90} value={age} onChange={e => setAge(Number(e.target.value))} className="w-full accent-brand-accent cursor-pointer" data-id="element-291" data-mp="1fs3q2y9lgaxp" />
            </div>

            <div data-id="element-292" data-mp="1tjs08w0a8pc1">
              <div className="flex justify-between items-baseline mb-2" data-id="element-293" data-mp="w2t7ncigrbvw">
                <label htmlFor="calc-years" className="text-sm font-semibold text-brand-100" data-id="element-294" data-mp="w0gqh1kj55jw">
                  Years of asbestos exposure
                </label>
                <span className="text-brand-accent font-bold" data-id="element-295" data-mp="tgsjxtubdzcu">
                  {years} {years === 1 ? 'year' : 'years'}
                </span>
              </div>
              <input id="calc-years" type="range" min={1} max={40} value={years} onChange={e => setYears(Number(e.target.value))} className="w-full accent-brand-accent cursor-pointer" data-id="element-296" data-mp="jjnvfnczxrsv" />
            </div>

            <div data-id="element-297" data-mp="5rr5npysvtzn">
              <div className="flex justify-between items-baseline mb-2" data-id="element-298" data-mp="5r7z15elos0x">
                <label htmlFor="calc-employers" className="text-sm font-semibold text-brand-100" data-id="element-299" data-mp="uhi6fuz1seg">
                  Identifiable employers or job sites
                </label>
                <span className="text-brand-accent font-bold" data-id="element-300" data-mp="3nyno18tvxoh1">
                  {employers}
                  {employers === 10 ? '+' : ''}
                </span>
              </div>
              <input id="calc-employers" type="range" min={1} max={10} value={employers} onChange={e => setEmployers(Number(e.target.value))} className="w-full accent-brand-accent cursor-pointer" data-id="element-301" data-mp="1aqcxc1psir2g" />
            </div>

            <label className="flex items-center gap-3 cursor-pointer pt-2" data-id="element-302" data-mp="15pyq2lw0jl5h">
              <input type="checkbox" checked={isVeteran} onChange={e => setIsVeteran(e.target.checked)} className="w-5 h-5 rounded accent-brand-accent cursor-pointer" data-id="element-303" data-mp="rzd7yzedvf8n" />
              <span className="text-sm text-brand-100" data-id="element-304" data-mp="i3ie184plxqk">
                Exposure occurred during U.S. military service
              </span>
            </label>
          </div>

          {/* Result */}
          <div className="lg:sticky lg:top-28 space-y-4" data-id="element-305" data-mp="933uad7e8qp6">
            <div className="bg-white rounded-2xl p-8 text-slate-900" data-id="element-306" data-mp="dz2cfj24pch5">
              <span className="text-xs font-bold tracking-widest uppercase text-brand-700" data-id="element-307" data-mp="3qjg5abzgbbmk">
                Estimated Compensation Range
              </span>

              <div className="mt-4 mb-2" data-id="element-308" data-mp="3lmg0e4ec8ex1">
                <div className="text-4xl md:text-5xl font-serif font-bold text-brand-900 leading-none" data-id="element-309" data-mp="3eqm6flu1fca">
                  {formatRange(estimate.low)}
                </div>
                <div className="text-sm font-semibold text-slate-500 my-2" data-id="element-310" data-mp="3vkvp60pw7c3d">
                  to
                </div>
                <div className="text-4xl md:text-5xl font-serif font-bold text-brand-900 leading-none" data-id="element-311" data-mp="8e3910cpemev">
                  {formatRange(estimate.high)}
                </div>
              </div>

              <p className="text-sm text-slate-600 leading-relaxed mt-6 mb-6" data-id="element-312" data-mp="i3wgbp5tcfdb">
                Compensation may come from multiple sources — defendant
                settlements, asbestos trust funds, VA benefits, and trial
                verdicts. Most California claims resolve within 12–18 months.
              </p>

              <div className="space-y-3" data-id="element-313" data-mp="qgh5llo6fcdp">
                <a href="tel:805-770-9317" className="w-full inline-flex justify-center items-center gap-2 bg-brand-accent hover:bg-brand-accentHover text-brand-900 px-6 py-4 rounded-md font-bold text-lg transition-colors" data-id="element-314" data-mp="qha1frllkhh2">
                  <Phone className="w-5 h-5" data-id="element-315" data-mp="bs482k9fxlh3" />
                  (805) 770-9317
                </a>
                <a href="#contact" className="w-full inline-flex justify-center items-center gap-2 bg-brand-900 hover:bg-brand-800 text-white px-6 py-4 rounded-md font-bold transition-colors" data-id="element-316" data-mp="19dry8nfaz7k6">
                  Get an Exact Valuation
                  <ArrowRight className="w-4 h-4" data-id="element-317" data-mp="uic64nya9lqf" />
                </a>
              </div>
            </div>

            <div className="flex gap-3 bg-white/5 border border-white/10 rounded-xl p-5" data-id="element-318" data-mp="3k7xiqih3c2q8">
              <Info className="w-5 h-5 text-brand-accent shrink-0 mt-0.5" data-id="element-319" data-mp="3f7i6ju8jp19u" />
              <p className="text-xs text-brand-100 leading-relaxed" data-id="element-320" data-mp="1uq0qmsmsvilj">
                This calculator provides a general estimate for informational
                purposes only. It is not a guarantee, promise, or prediction of
                any recovery. Actual compensation depends on your medical
                records, exposure evidence, and the solvency of the responsible
                companies.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}